<template>
  <div class="stock-movement">
    <!-- Quick periods -->
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <!-- Filters + actions -->
    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 5">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Warehouse</label>
          <FormDropdown v-model="filters.warehouse" :options="warehouseOptions" placeholder="All warehouses" clearable />
        </div>
        <div class="rp-filter">
          <label>Category</label>
          <FormDropdown v-model="filters.category" :options="categoryOptions" placeholder="All categories" clearable />
        </div>
        <div class="rp-filter">
          <label>Subcategory</label>
          <FormDropdown
            v-model="filters.sub_category"
            :options="subCategoryOptions"
            placeholder="All subcategories"
            clearable
          />
        </div>
        <div class="rp-filter rp-search">
          <label>Item</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input
              v-model="filters.search"
              type="text"
              class="form-control"
              placeholder="Search name, code, SKU or barcode"
            />
          </div>
        </div>
      </div>

      <ReportActions
        :loading="loading"
        :exporting="exporting"
        :can-reset="hasActiveFilters"
        :can-print="!!rows.length"
        @reset="resetFilters"
        @export="exportFile"
        @print="printReport"
      />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <!-- Summary cards: opening + in - out = closing -->
    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-navy">
        <span class="rp-card-icon"><i class="ti ti-archive"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Opening Stock</div>
          <div class="rp-card-value">{{ formatQty(summary.opening_qty) }}</div>
          <div class="rp-card-sub">{{ formatMoney(summary.opening_value) }} · on {{ formatDate(dayBefore) }}</div>
        </div>
      </div>
      <div class="rp-card tone-green">
        <span class="rp-card-icon"><i class="ti ti-arrow-bar-to-down"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Stock In</div>
          <div class="rp-card-value text-success">+{{ formatQty(summary.in_qty) }}</div>
          <div class="rp-card-sub">{{ formatMoney(summary.in_value) }}</div>
        </div>
      </div>
      <div class="rp-card tone-red">
        <span class="rp-card-icon"><i class="ti ti-arrow-bar-up"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Stock Out</div>
          <div class="rp-card-value text-danger">−{{ formatQty(summary.out_qty) }}</div>
          <div class="rp-card-sub">{{ formatMoney(summary.out_value) }}</div>
        </div>
      </div>
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-box"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Closing Stock</div>
          <div class="rp-card-value">{{ formatQty(summary.closing_qty) }}</div>
          <div class="rp-card-sub">
            {{ formatMoney(summary.closing_value) }} · net
            <span :class="netQty >= 0 ? 'text-success' : 'text-danger'">{{ netQty >= 0 ? '+' : '−' }}{{ formatQty(Math.abs(netQty)) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Insights -->
    <div class="rp-insights trend-first" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title d-flex justify-content-between align-items-center">
          <span>In vs out {{ summary.trend_unit === 'month' ? 'by month' : 'by day' }}</span>
          <span class="rp-legend">
            <span><i class="rp-dot in"></i>In</span>
            <span><i class="rp-dot out"></i>Out</span>
          </span>
        </div>
        <div v-show="summary.trend.length" class="rp-chart"><canvas ref="trendChart"></canvas></div>
        <p v-if="!summary.trend.length" class="text-muted small mb-0">No stock movement in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Movement by type</div>
        <div v-if="typeBars.length" class="rp-bars">
          <div v-for="type in typeBars" :key="type.type">
            <div class="d-flex justify-content-between small mb-1">
              <span class="text-truncate me-2">
                <i class="rp-dot" :class="type.direction"></i>{{ type.label }}
                <span class="text-muted">({{ formatInt(type.count) }})</span>
              </span>
              <span class="fw-semibold" :class="type.direction === 'in' ? 'text-success' : 'text-danger'">
                {{ type.direction === 'in' ? '+' : '−' }}{{ formatQty(type.qty) }}
              </span>
            </div>
            <div class="rp-bar-track">
              <div class="rp-bar-fill" :class="type.direction" :style="{ width: `${type.percent}%` }"></div>
            </div>
          </div>
        </div>
        <p v-else class="text-muted small mb-0">No stock movement in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Top movers</div>
        <ul v-if="summary.top_movers.length" class="rp-ranked">
          <li v-for="(mover, index) in summary.top_movers" :key="mover.name">
            <span class="rp-rank">{{ index + 1 }}</span>
            <span class="flex-grow-1 text-truncate" :title="mover.name">{{ mover.name }}</span>
            <span class="rp-chip in">+{{ formatQty(mover.in_qty) }}</span>
            <span class="rp-chip out">−{{ formatQty(mover.out_qty) }}</span>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">No stock movement in this period.</p>
        <div class="rp-note">
          <strong>{{ formatInt(summary.moved_items) }}</strong> of {{ formatInt(summary.total_items) }} items moved ·
          {{ formatInt(summary.movement_count) }} transactions
        </div>
      </div>
    </div>

    <!-- Movement table -->
    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="rp-pills">
          <button
            v-for="pill in movementPills"
            :key="pill.key"
            type="button"
            class="rp-pill"
            :class="{ active: movementFilter === pill.key }"
            @click="movementFilter = pill.key"
          >
            {{ pill.label }} <span class="rp-pill-count">{{ formatInt(pill.count) }}</span>
          </button>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th class="rp-expand-col"></th>
              <th v-for="column in columns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}
                  <i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in pagedRows" :key="row.key">
              <tr :class="{ expanded: expanded[row.key] }">
                <td class="rp-expand-col">
                  <button
                    v-if="row.transactions.length"
                    type="button"
                    class="rp-expand"
                    :title="expanded[row.key] ? 'Hide transactions' : 'Show transactions'"
                    @click="toggleRow(row.key)"
                  >
                    <i class="ti" :class="expanded[row.key] ? 'ti-chevron-down' : 'ti-chevron-right'"></i>
                  </button>
                </td>
                <td class="fw-semibold">{{ row.item_code }}</td>
                <td>
                  <div class="fw-medium">{{ row.item_name }}</div>
                  <small v-if="row.sku" class="text-muted">SKU: {{ row.sku }}</small>
                </td>
                <td>
                  <template v-if="row.variant">
                    <span v-for="part in row.variant.split(' · ')" :key="part" class="rp-variant">{{ part }}</span>
                  </template>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>{{ row.warehouse }}</td>
                <td class="text-end">{{ formatQty(row.opening_qty) }}</td>
                <td class="text-end">
                  <span v-if="Number(row.in_qty)" class="text-success fw-semibold">+{{ formatQty(row.in_qty) }}</span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="text-end">
                  <span v-if="Number(row.out_qty)" class="text-danger fw-semibold">−{{ formatQty(row.out_qty) }}</span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="text-end">
                  <span class="fw-semibold">{{ formatQty(row.closing_qty) }}</span>
                  <small v-if="row.unit" class="text-muted ms-1">{{ row.unit }}</small>
                </td>
                <td class="text-end fw-semibold">{{ formatMoney(row.closing_value) }}</td>
                <td class="text-center">
                  <span class="rp-count" :class="{ zero: !row.movement_count }">{{ row.movement_count }}</span>
                </td>
              </tr>

              <tr v-if="expanded[row.key]" class="rp-detail-row">
                <td></td>
                <td :colspan="columns.length">
                  <div class="rp-detail">
                    <div class="rp-detail-title"><i class="ti ti-arrows-exchange me-1"></i>Transactions</div>
                    <div class="table-responsive">
                      <table class="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Voucher</th>
                            <th>Type</th>
                            <th>Batch</th>
                            <th class="text-end">In</th>
                            <th class="text-end">Out</th>
                            <th class="text-end">Balance</th>
                            <th class="text-end">Unit Cost</th>
                            <th class="text-end">Value</th>
                            <th>Narration</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr class="text-muted">
                            <td colspan="6">Opening balance</td>
                            <td class="text-end fw-semibold">{{ formatQty(row.opening_qty) }}</td>
                            <td colspan="3"></td>
                          </tr>
                          <tr v-for="t in row.transactions" :key="t.id">
                            <td class="text-nowrap">{{ formatDate(t.date) }}</td>
                            <td class="text-nowrap">{{ t.voucher_id }}</td>
                            <td><span class="rp-type" :class="t.direction">{{ t.type_label }}</span></td>
                            <td>{{ t.batch_no || '—' }}</td>
                            <td class="text-end text-success">{{ Number(t.in_qty) ? formatQty(t.in_qty) : '' }}</td>
                            <td class="text-end text-danger">{{ Number(t.out_qty) ? formatQty(t.out_qty) : '' }}</td>
                            <td class="text-end fw-semibold">{{ formatQty(t.balance) }}</td>
                            <td class="text-end">{{ formatMoney(t.unit_cost) }}</td>
                            <td class="text-end">{{ formatMoney(t.value) }}</td>
                            <td class="rp-narration" :title="t.narration">{{ t.narration || '—' }}</td>
                          </tr>
                        </tbody>
                        <tfoot>
                          <tr>
                            <th colspan="4" class="text-end">Period total</th>
                            <th class="text-end text-success">{{ formatQty(row.in_qty) }}</th>
                            <th class="text-end text-danger">{{ formatQty(row.out_qty) }}</th>
                            <th class="text-end">{{ formatQty(row.closing_qty) }}</th>
                            <th colspan="3"></th>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </td>
              </tr>
            </template>

            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="columns.length + 1">
                <div class="rp-empty">
                  <i class="ti ti-arrows-exchange"></i>
                  <div class="fw-semibold">No stock movement found</div>
                  <small class="text-muted">Try another period, warehouse or search.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="visibleRows.length">
            <tr>
              <th></th>
              <th colspan="4" class="text-end">Total ({{ formatInt(visibleRows.length) }} rows)</th>
              <th class="text-end">{{ formatQty(visibleTotals.opening_qty) }}</th>
              <th class="text-end text-success">+{{ formatQty(visibleTotals.in_qty) }}</th>
              <th class="text-end text-danger">−{{ formatQty(visibleTotals.out_qty) }}</th>
              <th class="text-end">{{ formatQty(visibleTotals.closing_qty) }}</th>
              <th class="text-end">{{ formatMoney(visibleTotals.closing_value) }}</th>
              <th></th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination
        v-if="visibleRows.length"
        :page="page"
        :page-count="pageCount"
        :pages="pageNumbers"
        :start="pageStart"
        :end="pageEnd"
        :total="visibleRows.length"
        @go="goToPage"
      />
    </div>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport, { escapeHtml } from './inventoryReportMixin';
import periodReport, { defaultPeriod } from './periodReportMixin';

const EMPTY_SUMMARY = {
  opening_qty: 0, opening_value: 0, in_qty: 0, in_value: 0, out_qty: 0, out_value: 0,
  closing_qty: 0, closing_value: 0, total_items: 0, moved_items: 0, movement_count: 0,
  by_type: [], trend_unit: 'day', trend: [], top_movers: [],
};

export default {
  name: 'StockMovementReport',
  mixins: [inventoryReport, periodReport],

  reportUrl: apiUrls.stock_movement_report.get,
  fileName: 'stock_movement',
  columns: [
    { key: 'item_code', label: 'Item Code' },
    { key: 'item_name', label: 'Item Name' },
    { key: 'variant', label: 'Variant' },
    { key: 'warehouse', label: 'Warehouse' },
    { key: 'opening_qty', label: 'Opening', class: 'text-end', numeric: true },
    { key: 'in_qty', label: 'In', class: 'text-end', numeric: true },
    { key: 'out_qty', label: 'Out', class: 'text-end', numeric: true },
    { key: 'closing_qty', label: 'Closing', class: 'text-end', numeric: true },
    { key: 'closing_value', label: 'Closing Value', class: 'text-end', numeric: true },
    { key: 'movement_count', label: 'Moves', class: 'text-center', numeric: true },
  ],

  data() {
    return {
      filters: {
        ...defaultPeriod(), warehouse: null, category: null, sub_category: null, search: '',
      },
      summary: { ...EMPTY_SUMMARY },
      movementFilter: 'all',
      sortKey: 'item_name',
      sortDir: 'asc',
    };
  },

  computed: {
    hasActiveFilters() {
      return this.periodChanged || this.itemFiltersActive;
    },

    netQty() {
      return (Number(this.summary.in_qty) || 0) - (Number(this.summary.out_qty) || 0);
    },

    typeBars() {
      return this.withPercent(this.summary.by_type, 'qty');
    },

    movementPills() {
      const moved = this.rows.filter((r) => r.movement_count).length;
      return [
        { key: 'all', label: 'All', count: this.rows.length },
        { key: 'moved', label: 'With movement', count: moved },
        { key: 'in', label: 'Stock in', count: this.rows.filter((r) => Number(r.in_qty)).length },
        { key: 'out', label: 'Stock out', count: this.rows.filter((r) => Number(r.out_qty)).length },
        { key: 'idle', label: 'No movement', count: this.rows.length - moved },
      ];
    },

    visibleRows() {
      const filters = {
        moved: (r) => r.movement_count,
        in: (r) => Number(r.in_qty),
        out: (r) => Number(r.out_qty),
        idle: (r) => !r.movement_count,
      };
      const keep = filters[this.movementFilter];
      return this.sortRows(keep ? this.rows.filter(keep) : this.rows);
    },

    visibleTotals() {
      const fields = ['opening_qty', 'in_qty', 'out_qty', 'closing_qty', 'closing_value'];
      return this.visibleRows.reduce((totals, row) => {
        fields.forEach((field) => { totals[field] += Number(row[field]) || 0; });
        return totals;
      }, Object.fromEntries(fields.map((field) => [field, 0])));
    },
  },

  watch: {
    movementFilter() { this.page = 1; },
  },

  methods: {
    extraParams() {
      return this.periodParams();
    },

    resetExtraFilters() {
      this.movementFilter = 'all';
      return defaultPeriod(this.today);
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
      this.$nextTick(() => this.drawTrendChart(this.$refs.trendChart));
    },

    printReport() {
      const s = this.summary;
      const body = this.visibleRows.map((r) => `
        <tr>
          <td>${escapeHtml(r.item_code)}</td>
          <td>${escapeHtml(r.item_name)}${r.sku ? `<br><small>${escapeHtml(r.sku)}</small>` : ''}</td>
          <td>${escapeHtml(r.variant)}</td>
          <td>${escapeHtml(r.warehouse)}</td>
          <td class="num">${this.formatQty(r.opening_qty)}</td>
          <td class="num in">${this.formatQty(r.in_qty)}</td>
          <td class="num out">${this.formatQty(r.out_qty)}</td>
          <td class="num">${this.formatQty(r.closing_qty)}</td>
          <td class="num">${this.formatMoney(r.closing_value)}</td>
        </tr>`).join('');
      const t = this.visibleTotals;
      const table = `<table><thead><tr><th>Item Code</th><th>Item Name</th><th>Variant</th><th>Warehouse</th>
          <th class="num">Opening</th><th class="num">In</th><th class="num">Out</th><th class="num">Closing</th>
          <th class="num">Closing Value</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr><td colspan="4" class="num">Total</td><td class="num">${this.formatQty(t.opening_qty)}</td>
          <td class="num">${this.formatQty(t.in_qty)}</td><td class="num">${this.formatQty(t.out_qty)}</td>
          <td class="num">${this.formatQty(t.closing_qty)}</td><td class="num">${this.formatMoney(t.closing_value)}</td>
        </tr></tfoot></table>`;
      this.openPrint(
        'Stock Movement',
        [
          [this.periodText(), ...this.itemFiltersText()].join(' · '),
          `Printed ${new Date().toLocaleString()} · Stock value uses batch-wise costing`,
        ],
        [
          ['Opening Stock', `${this.formatQty(s.opening_qty)} · ${this.formatMoney(s.opening_value)}`],
          ['Stock In', `${this.formatQty(s.in_qty)} · ${this.formatMoney(s.in_value)}`],
          ['Stock Out', `${this.formatQty(s.out_qty)} · ${this.formatMoney(s.out_value)}`],
          ['Closing Stock', `${this.formatQty(s.closing_qty)} · ${this.formatMoney(s.closing_value)}`],
        ],
        table,
      );
    },
  },
};
</script>

<style scoped src="./inventoryReport.css"></style>
